import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { leaderboardApi, LeaderboardXPEntry } from '@/api/leaderboard';
import { Trophy, Medal, Award, TrendingUp, Users, Zap } from 'lucide-react';
import { useUserStore } from '@/stores/userStore';

export function LeaderboardPage() {
  const [leaderboard, setLeaderboard] = useState<LeaderboardXPEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const profile = useUserStore((state) => state.profile);
  const isBackendEnabled = useUserStore((state) => state.isBackendEnabled);

  useEffect(() => {
    loadLeaderboard();
  }, [isBackendEnabled]);

  async function loadLeaderboard() {
    if (!isBackendEnabled) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await leaderboardApi.getXPLeaderboard(100);
      setLeaderboard(data);
    } catch (err: any) {
      console.error('Failed to load leaderboard:', err);
      setError(err.message || 'Failed to load leaderboard');
    } finally {
      setIsLoading(false);
    }
  }

  function getRankIcon(rank: number) {
    if (rank === 1) {
      return <Trophy className="text-yellow-400" size={24} />;
    } else if (rank === 2) {
      return <Medal className="text-gray-300" size={24} />;
    } else if (rank === 3) {
      return <Medal className="text-orange-400" size={24} />;
    }
    return <span className="text-primary-300 font-semibold">#{rank}</span>;
  }

  function getRankBadge(rank: number) {
    if (rank === 1) return 'default';
    if (rank === 2) return 'info';
    if (rank === 3) return 'warning';
    return 'default';
  }

  if (!isBackendEnabled) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <Users className="mx-auto mb-4 text-primary-500" size={64} />
          <h2 className="text-2xl font-bold text-primary-400 mb-2">
            Sign In to View Leaderboard
          </h2>
          <p className="text-primary-200 mb-6">
            Connect to the cloud to compete with other players and see where you rank!
          </p>
          <a
            href="/login"
            className="inline-block bg-primary-500 hover:bg-primary-600 text-black font-semibold px-6 py-3 rounded-lg transition-all"
          >
            Sign In Now
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-primary-500/10 rounded-lg border border-primary-500/30">
            <Trophy className="text-primary-400" size={32} />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-primary-400 drop-shadow-[0_0_15px_rgba(34,197,94,0.5)]">
              Global Leaderboard
            </h1>
            <p className="text-primary-200/70 mt-1">
              Compete with developers worldwide
            </p>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card variant="outline">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="text-primary-400" size={24} />
                <div>
                  <div className="text-sm text-primary-300">Total Players</div>
                  <div className="text-2xl font-bold text-primary-400">
                    {leaderboard.length}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card variant="outline">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="text-success-400" size={24} />
                <div>
                  <div className="text-sm text-primary-300">Your XP</div>
                  <div className="text-2xl font-bold text-success-400">
                    {profile.xp.toLocaleString()}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card variant="outline">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Zap className="text-warning-400" size={24} />
                <div>
                  <div className="text-sm text-primary-300">Your Level</div>
                  <div className="text-2xl font-bold text-warning-400">
                    Level {profile.level}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Leaderboard */}
      <Card variant="elevated">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Award className="text-primary-400" size={24} />
              Top Players by XP
            </CardTitle>
            <Badge variant="info" size="sm">
              Top 100
            </Badge>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
              <p className="text-primary-300">Loading leaderboard...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-error-400 text-5xl mb-4">⚠️</div>
              <p className="text-error-300 mb-4">{error}</p>
              <button
                onClick={loadLeaderboard}
                className="bg-primary-500 hover:bg-primary-600 text-black px-6 py-2 rounded-lg font-semibold transition-all"
              >
                Retry
              </button>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto mb-4 text-primary-400" size={48} />
              <p className="text-primary-300">No players yet. Be the first!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Top 3 Special Display */}
              {leaderboard.slice(0, 3).map((entry) => (
                <div
                  key={entry.userId}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    entry.userId === profile.id
                      ? 'bg-primary-500/10 border-primary-500 ring-2 ring-primary-500/50'
                      : entry.rank === 1
                      ? 'bg-gradient-to-r from-yellow-500/10 to-transparent border-yellow-500/50'
                      : entry.rank === 2
                      ? 'bg-gradient-to-r from-gray-300/10 to-transparent border-gray-400/50'
                      : 'bg-gradient-to-r from-orange-500/10 to-transparent border-orange-500/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-black border-2 border-current">
                        {getRankIcon(entry.rank)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-lg text-primary-400">
                            {entry.username}
                          </span>
                          {entry.userId === profile.id && (
                            <Badge variant="success" size="sm">
                              You
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-primary-300">
                          Level {entry.level}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-success-400">
                        {entry.xp.toLocaleString()}
                      </div>
                      <div className="text-xs text-primary-300">XP</div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Rest of the leaderboard */}
              <div className="mt-4 space-y-1">
                {leaderboard.slice(3).map((entry) => (
                  <div
                    key={entry.userId}
                    className={`p-3 rounded-lg transition-all ${
                      entry.userId === profile.id
                        ? 'bg-primary-500/10 border border-primary-500 ring-1 ring-primary-500/50'
                        : 'bg-dark-100/50 hover:bg-dark-100 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 text-center">
                          <span className="text-primary-300 font-semibold">
                            #{entry.rank}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-primary-400">
                              {entry.username}
                            </span>
                            {entry.userId === profile.id && (
                              <Badge variant="success" size="sm">
                                You
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-primary-300">
                            Level {entry.level}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-success-400">
                          {entry.xp.toLocaleString()}
                        </div>
                        <div className="text-xs text-primary-300">XP</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
